import React from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../../api/auth";
import logo from "../../images/LogoNavbar1.svg";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("token") || "").trim();
  const [form, setForm] = React.useState({ newPassword: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token) {
      setError("El enlace de recuperación no es válido.");
      return;
    }
    if (form.newPassword.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await authApi.resetPassword({
        token,
        newPassword: form.newPassword,
      });
      setCompleted(true);
      setMessage(data?.message || "Contraseña actualizada. Ya podés iniciar sesión.");
      setForm({ newPassword: "", confirmPassword: "" });
    } catch (err) {
      setError(err.message || "No se pudo restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
        onClick={() => setShowPassword((value) => !value)}
        edge="end"
      >
        {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F6F7FB", display: "grid", placeItems: "center", px: 2, py: 4 }}>
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box component="img" src={logo} alt="WebProfit" sx={{ width: 64, height: 64, objectFit: "contain" }} />
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ width: 52, height: 52, mx: "auto", mb: 1.5, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                <LockResetRoundedIcon />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Nueva contraseña
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.75 }}>
                Creá una contraseña nueva de al menos 8 caracteres.
              </Typography>
            </Box>
          </Stack>

          {!token && (
            <Alert severity="error" sx={{ mb: 2 }}>
              El enlace no contiene un token válido. Solicitá uno nuevo.
            </Alert>
          )}
          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!completed && token ? (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Nueva contraseña"
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={form.newPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  autoComplete="new-password"
                  inputProps={{ minLength: 8, maxLength: 128 }}
                  InputProps={{ endAdornment: passwordAdornment }}
                />
                <TextField
                  label="Repetir nueva contraseña"
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  autoComplete="new-password"
                  inputProps={{ minLength: 8, maxLength: 128 }}
                />
                <Button type="submit" disabled={loading} variant="contained" size="large" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.25 }}>
                  {loading ? "Guardando..." : "Guardar contraseña"}
                </Button>
              </Stack>
            </Box>
          ) : completed ? (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate("/login", { replace: true, state: { message: "Contraseña actualizada correctamente." } })}
              sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.25 }}
            >
              Iniciar sesión
            </Button>
          ) : null}

          <Typography sx={{ textAlign: "center", mt: 3, color: "rgba(25,36,47,0.7)" }}>
            <Link component={RouterLink} to={token ? "/login" : "/olvide-contrasena"} sx={{ color: BRAND.orange, fontWeight: 900 }}>
              {token ? "Volver al inicio de sesión" : "Solicitar otro enlace"}
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
